import { syncQuery } from "serversync";
import { clickHref } from "settings/manage-abbreviations/buttons/ExportButton";
import { useLogin, yesNo } from "ui";
import { createDeferredPromise } from "utility";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ReplaceItem = {
  to?: string;
  next?: ReplaceList;
};

export type ReplaceList = {
  [key: string]: ReplaceItem;
};

export type StoredReplaceList = {
  name: string;
  _id: string;
  replacements: Record<string, string>;
  updatedAt: number;
};

type RemovedList = {
  name: string;
  _id: string;
  updatedAt: number;
  removed: number;
};

type ReplaceListStore = {
  active: ReplaceList;
  activeLists: StoredReplaceList[];
  lists: StoredReplaceList[];
  editList: string;
  removedLists: RemovedList[];
  showRemovedLists: boolean;
};

const replaceListsAreLoaded = createDeferredPromise();

export const useReplaceList = create<ReplaceListStore>()(
  persist(
    (get, set) => ({
      active: {},
      activeLists: [] as StoredReplaceList[],
      lists: [] as StoredReplaceList[],
      editList: "",
      removedLists: [],
      showRemovedLists: false,
    }),
    {
      name: "replaceListStore",
      version: 3,
      migrate(persistedState, version) {
        const oldState = persistedState as ReplaceListStore;
        if (version === 1) {
          oldState.activeLists = oldState.activeLists.map((list) => ({
            ...list,
            replacements: Object.fromEntries(
              (
                list.replacements as unknown as { from: string; to: string }[]
              ).map((item) => [item.from, item.to])
            ),
          }));
          oldState.lists = oldState.lists.map((list) => ({
            ...list,
            replacements: Object.fromEntries(
              (
                list.replacements as unknown as { from: string; to: string }[]
              ).map((item) => [item.from, item.to])
            ),
          }));
          oldState.removedLists = [];
        } else if (version === 2) {
          oldState.removedLists = [];
        }
        return { ...oldState };
      },
      partialize(state) {
        return {
          activeLists: state.activeLists,
          lists: state.lists,
        };
      },
      onRehydrateStorage() {
        return (state) => {
          if (state) {
            const activeLists = state.lists.filter(
              (list) =>
                !!state.activeLists.find(
                  (activeList) => activeList._id === list._id
                )
            );
            setTimeout(() => {
              setActiveReplaceLists(activeLists);
              replaceListsAreLoaded.resolve();
            });
          }
        };
      },
    }
  )
);

function addReplaceItem(target: ReplaceList, from: string, to: string) {
  if (!from || !target) return;

  const words = from.split(" ");
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    target[word] ??= {};

    if (i === words.length - 1) {
      target[word].to = to;
    } else {
      target[word].next ??= {};
      target = target[word].next;
    }
  }
}

export async function removeReplaceListItem(id: string, from: string) {
  const { lists } = useReplaceList.getState();
  const list = lists.find((list) => list._id === id);

  if (list) {
    delete list.replacements[from];
    const updatedAt = await syncQuery.query("modifyReplaceList", {
      id,
      add: {},
      remove: [from],
    });
    updateListTimestamp(id, updatedAt);
    useReplaceList.setState({
      lists: lists.map((l) => (l._id === list?._id ? { ...list } : l)),
    });
  }
}

export async function addReplaceListItem(id: string, from: string, to: string) {
  const { lists, activeLists } = useReplaceList.getState();
  const target = lists.find((list) => list._id === id);

  if (target) {
    target.replacements[from] = to;
    const updatedAt = await syncQuery.query("modifyReplaceList", {
      id,
      add: { [from]: to },
      remove: [],
    });
    target.updatedAt = updatedAt;
    useReplaceList.setState({
      lists: lists.map((list) => (list._id === id ? { ...target } : list)),
    });
    if (activeLists.includes(target)) {
      setActiveReplaceLists(activeLists);
    }
  }
}

function mergeReplaceLists(
  current: ReplaceList,
  add: StoredReplaceList
): ReplaceList {
  for (const [from, to] of Object.entries(add.replacements)) {
    addReplaceItem(current, from, to);
  }
  return current;
}

export function setActiveReplaceLists(activeLists: StoredReplaceList[]) {
  const newActive = {};

  for (const list of activeLists.toReversed()) {
    mergeReplaceLists(newActive, list);
  }
  useReplaceList.setState({ active: newActive, activeLists });
}

export async function addNewList(name: string) {
  const newList = await syncQuery.query("createReplaceList", {
    name,
    replacements: {},
  });

  useReplaceList.setState(({ lists }) => ({
    lists: [
      ...lists,
      { name, _id: newList.id, replacements: {}, updatedAt: newList.updatedAt },
    ],
  }));
}

export async function addStoredListFromServer(list: StoredReplaceList) {
  const { lists } = useReplaceList.getState();

  const existing = lists.find((l) => l._id === list._id);

  if (existing) {
    useReplaceList.setState({
      lists: lists.map((current) =>
        current._id === list._id ? list : current
      ),
    });
  } else {
    useReplaceList.setState({
      lists: [...lists, list],
    });
  }
}

export async function importStoredList(list: StoredReplaceList) {
  const { lists } = useReplaceList.getState();

  const existing = lists.find((l) => l._id === list._id);

  if (existing) {
    const replace = yesNo(
      `${list.name} already exists and was last modified ${new Date(
        existing.updatedAt
      ).toLocaleString()}. The list you are importing was last modified ${new Date(
        list.updatedAt
      ).toLocaleString()}. Do you want to replace the existing one?`
    );
    if (!replace) return;

    const updatedAt = await syncQuery.query("updateReplaceList", {
      id: list._id,
      list,
    });

    list.updatedAt = updatedAt;

    useReplaceList.setState({
      lists: lists.map((current) =>
        current._id === list._id ? list : current
      ),
    });
  } else {
    const newList = await syncQuery.query("createReplaceList", {
      name: list.name,
      replacements: list.replacements,
    });
    list._id = newList.id;
    list.updatedAt = newList.updatedAt;

    useReplaceList.setState({
      lists: [...lists, list],
    });
  }
}

export async function deleteList(id: string) {
  await syncQuery.query("removeReplaceList", { id });
  const { lists, removedLists } = useReplaceList.getState();
  const listToRemove = lists.find((list) => list._id === id);
  if (listToRemove) {
    useReplaceList.setState({
      lists: lists.filter((list) => list._id !== id),
      removedLists: [
        ...removedLists,
        {
          _id: listToRemove._id,
          name: listToRemove.name,
          updatedAt: listToRemove.updatedAt,
          removed: Date.now(),
        },
      ],
    });
  }
}

export async function permanentlyDeleteList(id: string) {
  await syncQuery.query("removeReplaceListPermanent", { id });
  useReplaceList.setState(({ removedLists }) => ({
    removedLists: removedLists.filter((list) => list._id !== id),
  }));
}

export async function recoverList(id: string) {
  const recoveredList = await syncQuery.query("recoverReplaceList", { id });
  useReplaceList.setState(({ lists, removedLists }) => ({
    lists: [...lists, recoveredList],
    removedLists: removedLists.filter((list) => list._id !== id),
  }));
}

function updateListId(from: string, to: string, updatedAt: number) {
  useReplaceList.setState(({ lists }) => ({
    lists: lists.map((current) =>
      current._id === from ? { ...current, _id: to, updatedAt } : current
    ),
  }));
}

function updateListTimestamp(id: string, updatedAt: number) {
  useReplaceList.setState(({ lists }) => ({
    lists: lists.map((current) =>
      current._id === id ? { ...current, updatedAt } : current
    ),
  }));
}

export function exportLists() {
  const { lists } = useReplaceList.getState();

  const downloadName = `replace_lists_${new Date().toLocaleDateString()}-backup.json`;
  const blob = new Blob([JSON.stringify(lists)], {
    type: "application/json",
  });
  clickHref(downloadName, blob);
}

export async function pushListToServer(list: StoredReplaceList) {
  if (list._id.length !== 24) {
    const { id, updatedAt } = await syncQuery.query("createReplaceList", list);
    updateListId(list._id, id, updatedAt);
  } else {
    const updatedAt = await syncQuery.query("updateReplaceList", {
      id: list._id,
      list,
    });
    updateListTimestamp(list._id, updatedAt);
  }
}

export async function getListFromServer(id: string) {
  const list = await syncQuery.query("getReplaceList", { id });

  if (list) {
    addStoredListFromServer(list);
  }
}

export async function syncReplaceListsWithServer() {
  await replaceListsAreLoaded;
  const listInfo = await syncQuery.query("getAllReplaceListsInfo");
  const { lists } = useReplaceList.getState();
  const { user } = useLogin.getState();

  if (!user?.roles.includes("ai-captions")) return;

  const removedLists: RemovedList[] = [];

  for (const info of listInfo) {
    const existing = lists.find((list) => list._id === info._id);
    if (info.removed) {
      removedLists.push(info);
    }

    if (existing) {
      console.log(existing.name, "exists");
      if (info.removed) {
        console.log("removing");
        deleteList(existing._id);
      } else if (existing.updatedAt < info.updatedAt) {
        console.log("get");
        getListFromServer(info._id);
      } else if (existing.updatedAt > info.updatedAt) {
        console.log("push");
        pushListToServer(existing);
      }
    } else if (!info.removed) {
      console.log(info, "does not exist");
      getListFromServer(info._id);
    }
  }

  useReplaceList.setState({ removedLists });

  for (const existing of lists) {
    if (!listInfo.find((list) => list._id === existing._id)) {
      console.log("Pushing", existing.name);
      pushListToServer(existing);
    }
  }
}
